import React from "react";
import styled from "styled-components";
import Modal from "../modal/Modal";

interface MarkdownHelpItem {
  syntax: string;
  description: string;
}

interface MarkdownHelpSection {
  section: string;
  items: MarkdownHelpItem[];
}

const Row = styled.div.attrs({ className: "u-row u-gap8" })`
  padding: 4px 0;
`;

const Syntax = styled.code`
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  padding: 0px 6px 0px 8px;
  background: var(--soremo-border);
  border: 1px solid var(--soremo-border);
  border-radius: 0px;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 96px;
  text-align: left;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 16px;
  width: 100%;
  align-self: stretch;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SectionBlock = styled.div.attrs({ className: "u-col" })`
  min-width: 0;
`;

const MARKDOWN_SECTIONS: MarkdownHelpSection[] = [
  {
    section: "見出し",
    items: [
      { syntax: "# 見出し1", description: "見出し（大）" },
      { syntax: "## 見出し2", description: "見出し（中）" },
      { syntax: "### 見出し3", description: "見出し（小）" },
    ],
  },
  {
    section: "装飾",
    items: [
      { syntax: "**テキスト**", description: "太字" },
      { syntax: "*テキスト*", description: "斜体" },
      { syntax: "[表示テキスト](URL)", description: "リンク" },
    ],
  },
  {
    section: "リスト",
    items: [
      { syntax: "- 項目", description: "箇条書き" },
      { syntax: "1. 項目", description: "番号付きリスト" },
      { syntax: "- [ ] タスク", description: "未完了タスク" },
      { syntax: "- [x] タスク", description: "完了タスク" },
    ],
  },
  {
    section: "コード・引用",
    items: [
      { syntax: "`コード`", description: "インラインコード" },
      { syntax: "```\nコードブロック\n```", description: "コードブロック" },
      { syntax: "> 引用文", description: "引用" },
    ],
  },
  {
    section: "表",
    items: [
      { syntax: "| A | B |", description: "1行目: ヘッダー" },
      { syntax: "|---|---|", description: "2行目: 区切り線" },
      { syntax: "| 1 | 2 |", description: "3行目〜: セル" },
    ],
  },
  {
    section: "おまけ",
    items: [
      { syntax: "[MM:SS]", description: "ここから再生" },
      { syntax: "[MM:SS.FF]", description: "フレーム指定で再生" },
      { syntax: "[300]", description: "絶対フレーム指定（先頭から300フレーム目）" },
    ],
  },
];

export interface MarkdownHelpModalProps {
  onClose: () => void;
}

const MarkdownHelpModal: React.FC<MarkdownHelpModalProps> = ({ onClose }) => {
  return (
    <Modal onClose={onClose} title="マークダウン早見表" size="default">
      <TwoColumnGrid>
        {MARKDOWN_SECTIONS.map(({ section, items }) => (
          <SectionBlock key={section}>
            <div className="heading-13 u-text-light-gray">{section}</div>
            {items.map(({ syntax, description }) => (
              <Row key={syntax}>
                <Syntax>{syntax}</Syntax>
                <span className="bodytext-13">{description}</span>
              </Row>
            ))}
          </SectionBlock>
        ))}
      </TwoColumnGrid>
    </Modal>
  );
};

export default MarkdownHelpModal;
